import _ from 'lodash';
import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter } from '@patsnap/tahiti-core';
import { EmbodimentRulesModel } from '../../models/embodiment-rules-model';
import type { EmbodimentRulesService } from '../embodiment-rules-service';
import type { EmbodimentRule, SelectedClaimFeature, SelectedKeyword } from '../../types';

@injectable()
export class EmbodimentRulesServiceImpl implements EmbodimentRulesService {
    private _onModelDataChangeEmitter = new Emitter<EmbodimentRule[]>();
    public readonly onStateChange = this._onModelDataChangeEmitter.event;
    private _onSubmitRulesEmitter = new Emitter<EmbodimentRule[]>();
    public readonly onSubmitRules = this._onSubmitRulesEmitter.event;

    constructor(
        @inject(EmbodimentRulesModel)
        private _model: EmbodimentRulesModel,
    ) {
        this._model.onStatesChange(this._emitModelDataChange.bind(this));
    }

    private _emitModelDataChange(): void {
        this._onModelDataChangeEmitter.fire(this.getModelData());
    }

    public getModelData(): EmbodimentRule[] {
        return this._model.modelData;
    }

    private _createRuleItem(): EmbodimentRule {
        return {
            id: _.uniqueId(),
            claimFeature: { num: '', feature: '' },
            keyword: { type: '', value: '' },
        };
    }

    public createRule(): void {
        const modelData = this.getModelData();
        this._model.update(() => [...modelData, this._createRuleItem()]);
    }

    public deleteRule(id: string): void {
        const modelData = this.getModelData();
        const newRules = modelData.filter(rule => rule.id !== id);

        // 如果删除后没有规则了，创建一个新的空规则
        if (newRules.length === 0) {
            newRules.push(this._createRuleItem());
        }

        this._model.update(() => newRules);
    }

    public updateRules(rules: EmbodimentRule[]): void {
        // 过滤掉没有设置关键词的规则
        const validRules = rules.filter(rule => rule.keyword.value);

        // 如果过滤后没有规则了，创建一个新的空规则
        if (validRules.length === 0) {
            validRules.push(this._createRuleItem());
        }

        this._model.update(() => validRules);
    }

    public updateFeature(id: string, feature: SelectedClaimFeature): void {
        const modelData = this.getModelData();
        const newRules = modelData.map(rule => rule.id === id ? { ...rule, claimFeature: feature } : rule);
        this._model.update(() => newRules);
    }

    public updateKeyword(id: string, keyword: SelectedKeyword): void {
        const modelData = this.getModelData();
        const newRules = modelData.map(rule => rule.id === id ? { ...rule, keyword: keyword } : rule);
        this._model.update(() => newRules);
    }

    public submitRules(): void {
        const modelData = this.getModelData();
        this._onSubmitRulesEmitter.fire(modelData.filter(rule => rule.keyword.value));
    }

    public resetRules(): void {
        this._model.update(() => [this._createRuleItem()]);
    }
}
